import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  useTheme,
} from '@mui/material';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import GroupsIcon from '@mui/icons-material/Groups';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import { buildRitmoAcsContext } from '../../utils/saude-context-text';

// Recado curto e motivacional por ACS/microarea. A IA sintetiza os numeros do contexto, nunca calcula.
const RITMO_PROMPT = `Voce e apoiador de gestao da Atencao Primaria. Escreva um recado curto, direto e motivacional para os ACS e o coordenador da unidade, a partir EXCLUSIVAMENTE dos dados fornecidos no contexto. NAO invente nem recalcule numeros — use apenas os valores do contexto (pacientes a visitar, dias uteis, ritmo por dia e microareas).

Produza:
1. Uma frase de abertura com o total a visitar, os dias uteis restantes e o ritmo sugerido por dia.
2. Um destaque das 2-3 microareas com maior numero de pacientes a visitar, sugerindo priorizar as visitas.
3. Uma frase final de incentivo, lembrando que visitar em ritmo constante evita o acumulo no fim do quadrimestre.

Tom pratico e encorajador, sem jargao. Maximo 6 linhas.`;

const formatDateBr = (iso?: string | null): string =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '-';

const SaudeRitmoVisitasAcs: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [quadrimestre, setQuadrimestre] = useState(getQuadrimestreAtual().quadrimestre);
  const [ano, setAno] = useState(getQuadrimestreAtual().ano);
  // Só busca dados quando há unidade selecionada (leve) OU o usuário opta explicitamente por
  // carregar o município inteiro (mais pesado). Nada roda ao entrar na página sem escolher.
  const [carregarTodas, setCarregarTodas] = useState(false);

  const unidadeParam = unidadeSelecionada?.codunidade;
  const podeBuscar = !!municipioAtivo?.id && (!!unidadeSelecionada || carregarTodas);

  useEffect(() => {
    if (!podeBuscar) { setData(null); return; }
    setLoading(true);
    setError(null);
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.getRitmoVisitasAcs(municipioAtivo!.id, qParam, unidadeParam)
      .then((res: any) => setData(res))
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar o ritmo de visitas.'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, quadrimestre, ano, unidadeParam, carregarTodas, podeBuscar]);

  const dadosContexto = useMemo(() => {
    if (!data || data.sem_dados || data.dwh_implementado === false) return undefined;
    return buildRitmoAcsContext(data);
  }, [data]);

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo?.id) return;
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.exportarRitmoAcsCsv(municipioAtivo.id, qParam, unidadeParam)
      .catch(() => setError('Falha ao exportar CSV.'));
  }, [municipioAtivo?.id, quadrimestre, ano, unidadeParam]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo?.id) return;
    const qParam = formatQuadrimestreParam(quadrimestre, ano);
    saudeService.exportarRitmoAcsPdf(municipioAtivo.id, qParam, unidadeParam)
      .catch(() => setError('Falha ao exportar PDF.'));
  }, [municipioAtivo?.id, quadrimestre, ano, unidadeParam]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para acessar o relatorio.</Alert></Box>;
  }

  const encerrado = data && data.dwh_implementado !== false && data.dias_uteis_restantes === 0;

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <DirectionsWalkIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#2e7d32' }} />
              Ritmo de Visitas do ACS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Por microarea: pacientes a visitar, dias uteis ate o fechamento e ritmo sugerido por dia
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={exportarCsv}
              disabled={loading || !data || data.sem_dados || data.dwh_implementado === false}>
              CSV
            </Button>
            <Button variant="outlined" size="small" color="error" startIcon={<PictureAsPdfIcon />} onClick={exportarPdf}
              disabled={loading || !data || data.sem_dados || data.dwh_implementado === false}>
              PDF
            </Button>
            <QuadrimestreSelector
              quadrimestre={quadrimestre}
              ano={ano}
              onChange={(q, a) => { setQuadrimestre(q); setAno(a); }}
            />
          </Box>
        </Box>

        {/* Escopo: unidade selecionada ou município inteiro */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {unidadeSelecionada ? (
            <Chip icon={<GroupsIcon sx={{ fontSize: 16 }} />} label={unidadeSelecionada.nome} variant="outlined" />
          ) : carregarTodas ? (
            <Chip icon={<GroupsIcon sx={{ fontSize: 16 }} />} label="Todas as unidades do municipio" variant="outlined" color="warning" />
          ) : null}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Gating: nada roda ate escolher unidade (ou optar por todas) */}
        {!podeBuscar ? (
          <Alert severity="warning" action={
            <Button color="inherit" size="small" onClick={() => setCarregarTodas(true)}>
              Carregar todas as unidades
            </Button>
          }>
            Selecione uma <strong>unidade</strong> no seletor da barra lateral para gerar o relatorio.
            Para o municipio inteiro (consulta mais pesada), use o botao ao lado.
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : data?.dwh_implementado === false ? (
          <Alert severity="info">
            O ritmo de visitas nao esta disponivel para este municipio (conexao via Data Warehouse).
          </Alert>
        ) : data && data.sem_dados ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            Nenhum paciente pendente de visita nos filtros selecionados. Territorio em dia.
          </Alert>
        ) : data ? (
          <>
            {/* Cards de topo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <DirectionsWalkIcon sx={{ color: '#2e7d32' }} />
                    <Typography variant="caption" color="text.secondary" display="block">Pacientes a visitar</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32' }}>{data.total_pacientes_pendentes}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <EventAvailableIcon sx={{ color: '#1565c0' }} />
                    <Typography variant="caption" color="text.secondary" display="block">Dias uteis restantes</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: encerrado ? '#c62828' : '#1565c0' }}>{data.dias_uteis_restantes}</Typography>
                    <Typography variant="caption" color="text.secondary">ate {formatDateBr(data.data_fechamento)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <SpeedIcon sx={{ color: '#e65100' }} />
                    <Typography variant="caption" color="text.secondary" display="block">Ritmo sugerido</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#e65100' }}>
                      {data.total_visitas_por_dia != null ? `~${data.total_visitas_por_dia}` : '--'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">visitas / dia util</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <GroupsIcon sx={{ color: '#7c4dff' }} />
                    <Typography variant="caption" color="text.secondary" display="block">Microareas com pendencia</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#7c4dff' }}>{data.microareas?.length ?? 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {encerrado && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Quadrimestre encerrado (sem dias uteis restantes). Os numeros abaixo refletem o gap acumulado no periodo.
              </Alert>
            )}

            {/* Lista de microareas */}
            {(data.microareas || []).map((mic: any, idx: number) => (
              <Accordion key={`${mic.micro}-${mic.equipe || ''}-${idx}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography fontWeight={600}>Microarea {mic.micro}</Typography>
                    {mic.equipe && <Typography variant="caption" color="text.secondary">Equipe {mic.equipe}</Typography>}
                    <Chip size="small" color="warning" label={`${mic.gap_visitas} a visitar`} />
                    {mic.visitas_por_dia != null && (
                      <Chip size="small" variant="outlined" icon={<SpeedIcon sx={{ fontSize: 15 }} />} label={`~${mic.visitas_por_dia}/dia`} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Paciente</TableCell>
                          <TableCell sx={{ width: 140 }}>Telefone</TableCell>
                          <TableCell>Acompanhamento</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(mic.pacientes || []).map((pac: any, i: number) => (
                          <TableRow key={pac.cns || pac.cpf || `${mic.micro}-${i}`} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>{pac.nome}</Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{pac.telefone || '--'}</TableCell>
                            <TableCell>
                              {(pac.populacoes || []).filter(Boolean).length === 0 ? (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {pac.populacoes.filter(Boolean).map((pop: string, k: number) => (
                                    <Chip key={k} size="small" variant="outlined" label={pop} />
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        ) : null}
      </Box>

      {municipioAtivo && dadosContexto && (
        <AnalisePredicaoCard
          dadosContexto={dadosContexto}
          municipioId={municipioAtivo.id}
          painelId="ritmo-visitas-acs"
          quadrimestre={quadrimestre}
          ano={ano}
          titulo="Recado da IA para os ACS"
          subtitulo="Ritmo de visitas, prioridades por microarea e incentivo"
          prompt={RITMO_PROMPT}
          manualTrigger
        />
      )}
    </>
  );
};

export default SaudeRitmoVisitasAcs;
